import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Check, Loader2, MailCheck } from "lucide-react";
import { RichText } from "@/components/RichText";
import { BrandWordmark } from "@/components/BrandWordmark";
import Seo from "@/components/Seo";
import api, { apiError } from "@/lib/api";
import { CreateathonBanner } from "@/components/CreateathonBanner";

function BlogEmailPrompt({ slug }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/blog/subscribe", { name, email, topic: slug });
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-blue-500/30 rounded-md bg-blue-600/10 p-6 my-10" data-testid="blog-email-prompt">
      {sent ? (
        <div className="flex items-center gap-3" data-testid="blog-email-sent">
          <div className="h-10 w-10 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <MailCheck className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <p className="text-sm font-semibold">You're in!</p>
            <p className="text-sm text-zinc-400">The next article on this topic lands in your inbox.</p>
          </div>
        </div>
      ) : (
        <>
          <h3 className="font-head text-lg font-bold mb-1">Want to see more on this topic?</h3>
          <p className="text-sm text-zinc-400 mb-4">Get the next practical article in your inbox — free, no spam, unsubscribe anytime.</p>
          {error && <p className="text-sm text-red-400 mb-3" data-testid="blog-email-error">{error}</p>}
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2.5">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="First name"
              data-testid="blog-email-name"
              className="sm:w-40 bg-[#0A0A0A] border border-white/10 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-zinc-600"
            />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="blog-email-input"
              className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-zinc-600"
            />
            <button
              disabled={loading}
              data-testid="blog-email-submit"
              className="bg-blue-600 hover:bg-blue-500 disabled:opacity-60 px-5 py-2.5 rounded-md text-sm font-semibold transition-colors"
            >
              {loading ? "Sending…" : "Send me more"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}

export default function BlogArticle() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    setPost(undefined);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null));
    api.post(`/blog/${slug}/view`).catch(() => {});
  }, [slug]);

  if (post === undefined) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center" data-testid="blog-article-loading">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }
  if (!post) return <Navigate to="/blog" replace />;
  const sections = post.sections || [];
  const promptAfter = Math.min(2, Math.max(0, sections.length - 1));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="blog-article-page">
      <CreateathonBanner />
      <Seo title={post.metaTitle} description={post.description} path={`/blog/${post.slug}`} />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                headline: post.title,
                description: post.description,
                datePublished: post.date,
                author: { "@type": "Organization", name: "Videoileo", url: "https://www.videoileo.com/" },
                publisher: { "@type": "Organization", name: "Videoileo", logo: { "@type": "ImageObject", url: "https://www.videoileo.com/videoileo-icon.png" } },
                mainEntityOfPage: `https://www.videoileo.com/blog/${post.slug}`,
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: "https://www.videoileo.com/" },
                  { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.videoileo.com/blog" },
                  { "@type": "ListItem", position: 3, name: post.title, item: `https://www.videoileo.com/blog/${post.slug}` },
                ],
              },
            ],
          })}
        </script>
      </Helmet>

      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <BrandWordmark className="h-12" />
          </Link>
          <Link to="/register" data-testid="blog-article-start-free" className="text-sm bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md font-medium transition-colors">
            Start free
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-12">
        <Link to="/blog" data-testid="blog-back-link" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1.5 mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" /> All articles
        </Link>
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">
          {post.category} · {post.readTime} · {post.date}
        </p>
        <h1 className="font-head text-3xl md:text-4xl tracking-tight font-black mb-10 leading-tight">{post.title}</h1>

        <article className="space-y-10">
          {sections.map((s, i) => (
            <div key={s.h}>
              <section>
                <h2 className="font-head text-lg md:text-xl font-semibold mb-3">{s.h}</h2>
                {(s.paras || []).map((p) => (
                  <p key={p.slice(0, 40)} className="text-sm md:text-base text-zinc-400 leading-relaxed mb-3"><RichText text={p} /></p>
                ))}
                {s.list && (
                  <ul className="space-y-2 mt-2">
                    {s.list.map((item) => (
                      <li key={item} className="text-sm md:text-base text-zinc-400 flex items-start gap-2 leading-relaxed">
                        <Check className="h-4 w-4 text-blue-500 shrink-0 mt-1" /> <span><RichText text={item} /></span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              {i === promptAfter && <BlogEmailPrompt slug={post.slug} />}
            </div>
          ))}
        </article>

        <div className="mt-14 border border-blue-500/30 rounded-md bg-blue-600/10 p-6" data-testid="blog-article-cta">
          <h2 className="font-head text-xl font-bold mb-2">Make your first faceless video free</h2>
          <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
            Videoileo generates the script, voiceover and cinematic visuals, renders the MP4 and schedules it to YouTube — sign up with just your email.
          </p>
          <Link to="/register" className="inline-block text-sm bg-blue-600 hover:bg-blue-500 px-5 py-2.5 rounded-md font-semibold transition-colors">
            Start free — no card required
          </Link>
          <Link to="/pricing" className="inline-block text-sm text-zinc-400 hover:text-white px-4 py-2.5 ml-1 transition-colors">
            See pricing
          </Link>
        </div>
      </main>

      <footer className="border-t border-white/10 py-10 px-5 text-center text-sm text-zinc-600">
        © 2026 Videoileo · The faceless video studio.
        <span className="mx-2">·</span>
        <Link to="/blog" className="text-zinc-500 hover:text-zinc-300 underline">Blog</Link>
        <span className="mx-2">·</span>
        <Link to="/guides" className="text-zinc-500 hover:text-zinc-300 underline">Guides</Link>
        <span className="mx-2">·</span>
        <Link to="/pricing" className="text-zinc-500 hover:text-zinc-300 underline">Pricing</Link>
      </footer>
    </div>
  );
}
